/**
 * PlanPicker — two plan cards (Single $14.99 / Family $24.99) as an accessible
 * radio group. Ported from the design prototype's ``PlanPicker``.
 *
 * Presentational only: emits ``onChange(planId)``; holds no billing logic.
 *
 * A11y: ``role="radiogroup"`` wrapper, each card ``role="radio"`` with
 * ``aria-checked``, keyboard-focusable, large tap targets (full card is the
 * target). Copy obeys the metered-usage copy guard (no limitless claims).
 */

import { ChromeTokens } from "../chrome/tokens";
import { Icon } from "../chrome/Icon";
import { WBM_PLANS, money } from "./plans";
import type { PlanId } from "./types";

export interface PlanPickerProps {
  value: PlanId;
  onChange: (id: PlanId) => void;
  /** Compact layout hides the per-plan feature list (used inside takeovers). */
  compact?: boolean;
}

export function PlanPicker({ value, onChange, compact = false }: PlanPickerProps): JSX.Element {
  return (
    <div
      role="radiogroup"
      aria-label="Choose a plan"
      style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ChromeTokens.spacing.md }}
    >
      {WBM_PLANS.map((p) => {
        const active = value === p.id;
        return (
          <button
            key={p.id}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={`${p.name} plan, ${money(p.price)} per month, ${p.per}`}
            onClick={() => onChange(p.id)}
            style={{
              position: "relative",
              textAlign: "left",
              cursor: "pointer",
              padding: compact ? ChromeTokens.spacing.lg - 2 : ChromeTokens.spacing.lg,
              borderRadius: ChromeTokens.radius.lg,
              background: ChromeTokens.color.base,
              fontFamily: ChromeTokens.font.ui,
              color: ChromeTokens.color.ink,
              minHeight: 44,
              border: active
                ? `2.5px solid ${ChromeTokens.color.sun}`
                : `2px solid ${ChromeTokens.color.baseMuted}`,
              boxShadow: active ? ChromeTokens.shadow.soft : "none",
              transition: `border-color 160ms ${ChromeTokens.motion.softEase}, box-shadow 160ms ${ChromeTokens.motion.softEase}`,
            }}
          >
            {p.rec && (
              <span
                style={{
                  position: "absolute",
                  top: -11,
                  right: ChromeTokens.spacing.md,
                  fontSize: 10,
                  fontWeight: 800,
                  color: ChromeTokens.color.ink,
                  background: ChromeTokens.color.grass,
                  padding: "3px 9px",
                  borderRadius: ChromeTokens.radius.pill,
                  letterSpacing: ".03em",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <Icon name="star" size={10} stroke={ChromeTokens.color.ink} /> POPULAR
              </span>
            )}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: ChromeTokens.spacing.sm }}>
              <strong style={{ fontSize: 16 }}>{p.name}</strong>
              <span
                aria-hidden="true"
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: ChromeTokens.radius.pill,
                  flexShrink: 0,
                  display: "grid",
                  placeItems: "center",
                  border: active ? "none" : `2px solid ${ChromeTokens.color.baseMuted}`,
                  background: active ? ChromeTokens.color.sun : "transparent",
                }}
              >
                {active && <Icon name="check" size={13} stroke={ChromeTokens.color.ink} />}
              </span>
            </div>
            <div style={{ fontSize: 12.5, color: ChromeTokens.color.inkMuted, marginTop: 1 }}>{p.blurb}</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: ChromeTokens.spacing.xs, marginTop: ChromeTokens.spacing.sm + 2 }}>
              <span style={{ fontSize: 26, fontWeight: 900, letterSpacing: "-.02em" }}>{money(p.price)}</span>
              <span style={{ fontSize: 12.5, color: ChromeTokens.color.inkMuted, fontWeight: 700 }}>/mo</span>
            </div>
            <div style={{ fontSize: 11.5, color: ChromeTokens.color.inkMuted, fontWeight: 700, marginTop: 1 }}>{p.per}</div>
            {!compact && (
              <ul style={{ listStyle: "none", margin: `${ChromeTokens.spacing.md}px 0 0`, padding: 0, display: "flex", flexDirection: "column", gap: ChromeTokens.spacing.sm - 2 }}>
                {p.features.map((f) => (
                  <li key={f} style={{ display: "flex", gap: 7, alignItems: "flex-start", fontSize: 12.5, lineHeight: 1.35 }}>
                    <Icon name="check" size={14} stroke={ChromeTokens.color.grass} style={{ flexShrink: 0, marginTop: 1 }} /> {f}
                  </li>
                ))}
              </ul>
            )}
          </button>
        );
      })}
    </div>
  );
}
